import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { DatePicker, Select, TimeRangePickerProps } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useState } from 'react';
import './index.scss';

type PayPeriod = {
	id: string;
	name: string;
	startDate: string;
	endDate: string;
};

type Props = {
	name: string;
	label?: string;
	value: [string, string];
	required: boolean;
	isError: boolean;
	onChange: (value: [string, string]) => void;
	helperText?: string;
	disabled?: boolean;
	className?: string;
	showNavigationArrows?: boolean;
	payPeriods?: PayPeriod[];
	currentPayPeriodIndex?: number;
	onPayPeriodChange?: (index: number, payPeriod: PayPeriod) => void;
	onNavigateBack?: () => void;
	onNavigateForward?: () => void;
	disableBackNavigation?: boolean;
	disableForwardNavigation?: boolean;
	allowCustomDateSelection?: boolean;
	payPeriodsForNavigation: any;
	width: string;
};

const DateRangePickerField = (props: Props) => {
	const { RangePicker } = DatePicker;
	const {
		name,
		label,
		value,
		className,
		isError = false,
		required = false,
		onChange,
		disabled = false,
		helperText = 'Date is required',
		showNavigationArrows = false,
		payPeriods = [],
		currentPayPeriodIndex = 0,
		onPayPeriodChange,
		onNavigateBack,
		onNavigateForward,
		disableBackNavigation = false,
		disableForwardNavigation = false,
		allowCustomDateSelection = true,
		payPeriodsForNavigation,
		width,
	} = props;

	const handleChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
		if (dates && dates[0] && dates[1]) {
			onChange([dates[0].toISOString(), dates[1].toISOString()]);
		} else {
			onChange(['', '']);
		}
	};

	// Get current pay period index in the navigation array
	const getCurrentNavigationIndex = () => {
		if (!value[0] || !value[1] || payPeriodsForNavigation.length === 0)
			return -1;

		return payPeriodsForNavigation.findIndex((period: any) => {
			const periodStart = dayjs(period.startDate).format('YYYY-MM-DD');
			const periodEnd = dayjs(period.endDate).format('YYYY-MM-DD');
			const valueStart = dayjs(value[0]).format('YYYY-MM-DD');
			const valueEnd = dayjs(value[1]).format('YYYY-MM-DD');
			return periodStart === valueStart && periodEnd === valueEnd;
		});
	};

	const handleNavigateBack = () => {
		if (onNavigateBack) {
			onNavigateBack();
		} else if (payPeriodsForNavigation.length > 0) {
			const currentNavIndex = getCurrentNavigationIndex();
			if (currentNavIndex > 0) {
				// Go to previous pay period (earlier in time)
				const newNavIndex = currentNavIndex - 1;
				const newPayPeriod = payPeriodsForNavigation[newNavIndex];

				// Find the index in the original payPeriods array for callback
				const originalIndex = payPeriods.findIndex(
					(p) => p.id === newPayPeriod.id
				);

				if (onPayPeriodChange && originalIndex !== -1) {
					onPayPeriodChange(originalIndex, newPayPeriod);
				}
				onChange([newPayPeriod.startDate, newPayPeriod.endDate]);
			}
		}
	};

	const handleNavigateForward = () => {
		if (onNavigateForward) {
			onNavigateForward();
		} else if (payPeriodsForNavigation.length > 0) {
			const currentNavIndex = getCurrentNavigationIndex();
			if (
				currentNavIndex < payPeriodsForNavigation.length - 1 &&
				currentNavIndex !== -1
			) {
				// Go to next pay period (later in time)
				const newNavIndex = currentNavIndex + 1;
				const newPayPeriod = payPeriodsForNavigation[newNavIndex];

				// Find the index in the original payPeriods array for callback
				const originalIndex = payPeriods.findIndex(
					(p) => p.id === newPayPeriod.id
				);

				if (onPayPeriodChange && originalIndex !== -1) {
					onPayPeriodChange(originalIndex, newPayPeriod);
				}
				onChange([newPayPeriod.startDate, newPayPeriod.endDate]);
			}
		}
	};

	const isBackDisabled = () => {
		if (disableBackNavigation || disabled) return true;
		if (payPeriodsForNavigation.length > 0) {
			const currentNavIndex = getCurrentNavigationIndex();
			return currentNavIndex <= 0;
		}
		return false;
	};

	const isForwardDisabled = () => {
		if (disableForwardNavigation || disabled) return true;
		if (payPeriodsForNavigation.length > 0) {
			const currentNavIndex = getCurrentNavigationIndex();
			return (
				currentNavIndex >= payPeriodsForNavigation.length - 1 ||
				currentNavIndex === -1
			);
		}
		return false;
	};

	// const handleNavigateBack = () => {
	// 	if (onNavigateBack) {
	// 		onNavigateBack();
	// 	} else if (payPeriods.length > 0 && currentPayPeriodIndex > 0) {
	// 		const newIndex = currentPayPeriodIndex - 1;
	// 		const newPayPeriod = payPeriods[newIndex];
	// 		if (onPayPeriodChange) {
	// 			onPayPeriodChange(newIndex, newPayPeriod);
	// 		}
	// 		onChange([newPayPeriod.startDate, newPayPeriod.endDate]);
	// 	}
	// };

	// const handleNavigateForward = () => {
	// 	if (onNavigateForward) {
	// 		onNavigateForward();
	// 	} else if (
	// 		payPeriods.length > 0 &&
	// 		currentPayPeriodIndex < payPeriods.length - 1
	// 	) {
	// 		const newIndex = currentPayPeriodIndex + 1;
	// 		const newPayPeriod = payPeriods[newIndex];
	// 		if (onPayPeriodChange) {
	// 			onPayPeriodChange(newIndex, newPayPeriod);
	// 		}
	// 		onChange([newPayPeriod.startDate, newPayPeriod.endDate]);
	// 	}
	// };

	// const isBackDisabled = () => {
	// 	if (disableBackNavigation || disabled) return true;
	// 	if (payPeriods.length > 0) {
	// 		return currentPayPeriodIndex <= 0;
	// 	}
	// 	return false;
	// };

	// const isForwardDisabled = () => {
	// 	if (disableForwardNavigation || disabled) return true;
	// 	if (payPeriods.length > 0) {
	// 		return currentPayPeriodIndex >= payPeriods.length - 1;
	// 	}
	// 	return false;
	// };

	const getMatchedPayPeriod = () => {
		if (!value[0] || !value[1] || payPeriods.length === 0) return null;

		return payPeriods.find((period) => {
			const periodStart = dayjs(period.startDate).format('YYYY-MM-DD');
			const periodEnd = dayjs(period.endDate).format('YYYY-MM-DD');
			const valueStart = dayjs(value[0]).format('YYYY-MM-DD');
			const valueEnd = dayjs(value[1]).format('YYYY-MM-DD');

			return periodStart === valueStart && periodEnd === valueEnd;
		});
	};

	const getDisplayValue = () => {
		if (!value[0] || !value[1]) return '';

		const matchedPeriod = getMatchedPayPeriod();
		if (matchedPeriod) {
			return `${matchedPeriod.name} `;
		}

		return `${dayjs(value[0]).format('DD/MMM/YYYY')} - ${dayjs(
			value[1]
		).format('DD/MMM/YYYY')}`;
	};

	const handlePresetSelect = (selectedValue: string) => {
		const selectedPeriod = payPeriods.find((p) => p.id === selectedValue);
		if (selectedPeriod) {
			onChange([selectedPeriod.startDate, selectedPeriod.endDate]);
			if (onPayPeriodChange) {
				const index = payPeriods.findIndex(
					(p) => p.id === selectedValue
				);
				onPayPeriodChange(index, selectedPeriod);
			}
		}
	};

	const getSelectedPeriodId = () => {
		const matchedPeriod = getMatchedPayPeriod();
		return matchedPeriod ? matchedPeriod.id : undefined;
	};

	const rangePresets: TimeRangePickerProps['presets'] = [
		...payPeriods
			.slice()
			.reverse()
			.map((period) => ({
				label: `${period.name}`,
				value: [dayjs(period.startDate), dayjs(period.endDate)] as [
					Dayjs,
					Dayjs
				],
			})),
	];

	return (
		<div className="date-range-picker-field">
			{label && (
				<p className="input-label">
					{label}{' '}
					{required && <span className="required-asterisk">*</span>}
				</p>
			)}

			<div className="date-range-container" style={{ width: width }}>
				{!allowCustomDateSelection && payPeriods.length > 0 ? (
					<div className="date-range-wrapper">
						<Select
							size="large"
							className={`pay-period-dropdown ${
								showNavigationArrows
									? 'with-navigation-arrows'
									: ''
							} ${isError ? 'error' : ''} ${className || ''}`}
							placeholder="Select pay period"
							value={getSelectedPeriodId()}
							onChange={handlePresetSelect}
							disabled={disabled}
							options={payPeriods
								.slice()
								.reverse()
								.map((period) => ({
									label: period.name,
									value: period.id,
								}))}
						/>

						{showNavigationArrows && (
							<>
								<button
									type="button"
									onClick={handleNavigateBack}
									disabled={isBackDisabled()}
									className={`nav-button nav-left ${
										isBackDisabled() ? 'disabled' : ''
									}`}
								>
									<LeftOutlined />
								</button>

								<button
									type="button"
									onClick={handleNavigateForward}
									disabled={isForwardDisabled()}
									className={`nav-button nav-right ${
										isForwardDisabled() ? 'disabled' : ''
									}`}
								>
									<RightOutlined />
								</button>
							</>
						)}
					</div>
				) : showNavigationArrows ? (
					<div className="date-range-wrapper">
						<div className="custom-display-input">
							<span className="display-text">
								{getDisplayValue() || 'Select date range'}
							</span>
						</div>

						<RangePicker
							className="hidden-range-picker"
							format={'DD/MMM/YYYY'}
							inputReadOnly={!allowCustomDateSelection}
							presets={
								allowCustomDateSelection
									? rangePresets
									: undefined
							}
							name={name}
							value={
								value[0] && value[1]
									? [dayjs(value[0]), dayjs(value[1])]
									: null
							}
							onChange={handleChange}
							disabled={disabled}
							allowClear={false}
							size="middle"
							style={{
								width: '100%',
								paddingLeft: '40px',
								paddingRight: '40px',
							}}
							dropdownClassName="date-range-dropdown"
						/>

						<button
							type="button"
							onClick={handleNavigateBack}
							disabled={isBackDisabled()}
							className={`nav-button nav-left ${
								isBackDisabled() ? 'disabled' : ''
							}`}
						>
							<LeftOutlined />
						</button>

						<button
							type="button"
							onClick={handleNavigateForward}
							disabled={isForwardDisabled()}
							className={`nav-button nav-right ${
								isForwardDisabled() ? 'disabled' : ''
							}`}
						>
							<RightOutlined />
						</button>
					</div>
				) : (
					<RangePicker
						className={`date-range-picker ${
							isError ? 'error' : ''
						} ${className || ''}`}
						format={'DD/MMM/YYYY'}
						name={name}
						value={
							value[0] && value[1]
								? [dayjs(value[0]), dayjs(value[1])]
								: null
						}
						onChange={handleChange}
						presets={rangePresets}
						disabled={disabled}
						allowClear={false}
						size="middle"
						dropdownClassName="date-range-dropdown"
					/>
				)}

				{isError && (
					<p className="date-range-error-message">{helperText}</p>
				)}
			</div>
		</div>
	);
};

export default DateRangePickerField;
